import React, { Fragment, useEffect, useState, useRef } from 'react';
import { FaMapPin, FaUser, FaHome, FaMapMarkerAlt, FaLandmark, FaCity, FaPhone } from 'react-icons/fa';
import { IoMdArrowRoundBack } from 'react-icons/io';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { useForm } from 'react-hook-form';

function NewAddressPage() {
    const navigate = useNavigate();
    const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm();
    const [mobile, setMobile] = useState("");
    const [loading, setLoading] = useState(false);
    const [mapLoading, setMapLoading] = useState(true);
    const [mapError, setMapError] = useState("");
    const [locating, setLocating] = useState(false);
    const [locErrorFlash, setLocErrorFlash] = useState(false);
    const mapRef = useRef(null); // div ref
    const autocompleteInputRef = useRef(null);
    const googleMapRef = useRef(null);
    const API_URL = process.env.REACT_APP_API_URL;
    // Hardcoded per user request (note: not recommended to commit secrets)
    const GOOGLE_KEY = "AIzaSyCde5_sjnqvTVIsB0pOAEQtOVwBV_jzeI8";

    useEffect(() => {
        const storedMobile = localStorage.getItem("mobile");
        if (storedMobile) {
            setMobile(storedMobile);
        } else {
            toast.error("Mobile number not found. Please verify your number first.");
            navigate("/");
        }
    }, [navigate]);

    // Load Google Maps script & init map
    const mapInitializedRef = useRef(false);
    useEffect(() => {
        if (!GOOGLE_KEY) {
            setMapError('Google Maps API key missing.');
            setMapLoading(false);
            return;
        }

        const handleLoad = () => {
            if (mapInitializedRef.current) return; // guard
            if (window.google && window.google.maps) {
                initMap();
                mapInitializedRef.current = true;
            }
        };

        if (window.google && window.google.maps) {
            handleLoad();
            return; // already loaded
        }

        const existing = document.getElementById('google-maps-script');
        if (existing) {
            // If already loaded, initialize immediately
            if (existing.getAttribute('data-loaded') === 'true') {
                handleLoad();
            } else {
                existing.addEventListener('load', handleLoad, { once: true });
            }
            return () => existing.removeEventListener('load', handleLoad);
        }

        const script = document.createElement('script');
        script.id = 'google-maps-script';
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_KEY}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = () => { script.setAttribute('data-loaded', 'true'); handleLoad(); };
        script.onerror = () => { setMapError('Failed to load Google Maps'); setMapLoading(false); };
        document.body.appendChild(script);
        return () => {
            script.onload = null;
            script.onerror = null;
        };
    }, [GOOGLE_KEY]);

    // Cleanup map on unmount
    useEffect(() => {
        return () => {
            googleMapRef.current = null;
        };
    }, []);

    const initMap = () => {
        if (!mapRef.current || !window.google) return;
        try {
            const center = { lat: 28.367, lng: 79.430 }; // Bareilly approx
            const map = new window.google.maps.Map(mapRef.current, {
                center,
                zoom: 14,
                disableDefaultUI: true,
                zoomControl: false,
            });
            googleMapRef.current = map;
            // Initialize form with starting center
            setValue('latitude', center.lat);
            setValue('longitude', center.lng);

            // Update lat/lng when map movement stops
            map.addListener('idle', () => {
                const c = map.getCenter();
                if (!c) return;
                setValue('latitude', c.lat());
                setValue('longitude', c.lng());
            });

            // Autocomplete
            if (autocompleteInputRef.current) {
                const autocomplete = new window.google.maps.places.Autocomplete(autocompleteInputRef.current, {
                    fields: ['formatted_address', 'geometry', 'address_components']
                });
                autocomplete.addListener('place_changed', () => {
                    const place = autocomplete.getPlace();
                    if (!place.geometry || !place.geometry.location) return;
                    const loc = place.geometry.location;
                    const lat = loc.lat();
                    const lng = loc.lng();
                    map.panTo({ lat, lng });
                    setValue('latitude', lat);
                    setValue('longitude', lng);
                    // Attempt to extract components -> house / area / landmark
                    if (place.address_components) {
                        const comps = place.address_components;
                        const getComp = (types) => {
                            const c = comps.find(ac => types.every(t => ac.types.includes(t)));
                            return c ? c.long_name : '';
                        };
                        const route = getComp(['route']);
                        const street_number = getComp(['street_number']);
                        const locality = getComp(['locality']);
                        const sublocality = getComp(['sublocality_level_1']);
                        if (route || street_number) {
                            setValue('house', [street_number, route].filter(Boolean).join(' '));
                        }
                        if (sublocality || locality) {
                            setValue('area', (sublocality || locality));
                        }
                    }
                });
            }
            setMapLoading(false);
        } catch (e) {
            console.error(e);
            setMapError('Error initializing map');
            setMapLoading(false);
        }
    };

    // Geolocation + reverse geocode autofill
    const geoTriedRef = useRef(false);
    useEffect(() => {
        if (mapLoading || mapError || !window.google || !googleMapRef.current) return;
        if (geoTriedRef.current) return;
        geoTriedRef.current = true;
        if (!navigator.geolocation) {
            return; // silently skip
        }
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const lat = pos.coords.latitude;
                const lng = pos.coords.longitude;
                setValue('latitude', lat);
                setValue('longitude', lng);
                if (googleMapRef.current) googleMapRef.current.panTo({ lat, lng });
                // Reverse geocode
                try {
                    const geocoder = new window.google.maps.Geocoder();
                    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                        if (status === 'OK' && results && results[0]) {
                            const comps = results[0].address_components || [];
                            const getComp = (types) => {
                                const c = comps.find(ac => types.every(t => ac.types.includes(t)));
                                return c ? c.long_name : '';
                            };
                            const route = getComp(['route']);
                            const street_number = getComp(['street_number']);
                            const locality = getComp(['locality']);
                            const sublocality = getComp(['sublocality_level_1']);
                            const premise = getComp(['premise']);
                            // Only set if empty so we don't overwrite user typing
                            if (!watch('house')) {
                                const houseVal = [street_number, route, premise].filter(Boolean).join(' ');
                                if (houseVal) setValue('house', houseVal.trim());
                            }
                            if (!watch('area')) {
                                const areaVal = sublocality || locality;
                                if (areaVal) setValue('area', areaVal);
                            }
                        }
                    });
                } catch (e) {
                    // ignore geocoder errors
                }
            },
            () => {
                // user denied or failed; no action
            },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
        );
    }, [mapLoading, mapError, setValue, watch]);

    const onSubmit = async (data) => {
        setLoading(true);
        try {
            if (!data.latitude || !data.longitude) {
                toast.error('Please pick a location on the map.');
                setLoading(false);
                return;
            }
            await axios.put(`${API_URL}/add-address/${mobile}`, data);
            toast.success("Address added successfully!");
            navigate(-1);
        } catch (error) {
            console.error("Error adding address:", error);
            toast.error("Failed to add address. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const recenterToCurrent = () => {
        if (locating) return; // prevent double clicks
        if (!navigator.geolocation) {
            toast.error('Geolocation not supported');
            return;
        }
        if (!googleMapRef.current) return;
        setLocating(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const lat = pos.coords.latitude;
                const lng = pos.coords.longitude;
                googleMapRef.current.panTo({ lat, lng });
                setValue('latitude', lat);
                setValue('longitude', lng);
                setLocating(false);
            },
            () => {
                toast.error('Unable to fetch current location');
                setLocating(false);
                setLocErrorFlash(true);
                setTimeout(() => setLocErrorFlash(false), 1200);
            },
            { enableHighAccuracy:true, timeout:8000, maximumAge:60000 }
        );
    };

    return (
        <Fragment>
            <div className="container-fluid m-t25">
                <div className="row">
                    <div className="col-12 cat-strip border shadow-sm">
                        <div>
                            <button onClick={() => navigate(-1)}><IoMdArrowRoundBack /></button>
                            <label>ADD NEW ADDRESS</label>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-lg-12 col-md-12 col-sm-12 col-12 add-add-form">
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <div className="mb-2">
                                <div className="bb-field bb-search mb-2 mt-3">
                                    <FaMapMarkerAlt className="bb-ico" />
                                    <input
                                        ref={autocompleteInputRef}
                                        type="text"
                                        placeholder="Search address / landmark"
                                    />
                                </div>
                                <div className="map-wrapper mb-2 position-relative">
                                    <div ref={mapRef} className="map-container" />
                                    {/* Center pin icon */}
                                    <div className="map-center-pin">
                                        <FaMapPin className="map-pin-icon" />
                                    </div>
                                    <button
                                        type="button"
                                        className={`current-loc-btn ${locating? 'loading':''} ${locErrorFlash? 'error-flash':''} pulse`}
                                        onClick={recenterToCurrent}
                                        title="Use my location"
                                        aria-label="Use my current location"
                                        disabled={locating}
                                    >
                                        {locating ? <span className="loc-spinner" /> : <FaMapMarkerAlt />}
                                    </button>
                                </div>
                                {mapLoading && !mapError && <div className='p-1 small text-muted'>Loading map...</div>}
                                {mapError && <div className='p-1 small text-danger'>{mapError}</div>}
                                {!mapLoading && !mapError && <div className='p-1 small text-secondary' style={{fontSize:'12px'}}>Drag the map so the red pin points to your exact delivery location. You can also search above.</div>}
                            </div>
                            <input type="hidden" {...register('latitude', { required: 'Lat required' })} />
                            <input type="hidden" {...register('longitude', { required: 'Lng required' })} />

                            <div className={`bb-field ${errors.name? 'error':''}`}>
                                <FaUser className="bb-ico" />
                                <input type="text" placeholder="Full Name *" {...register('name',{required:'Name is required'})} />
                                {errors.name && <span className="bb-msg">{errors.name.message}</span>}
                            </div>
                            <div className={`bb-field ${errors.house? 'error':''}`}>
                                <FaHome className="bb-ico" />
                                <input type="text" placeholder="House / Street *" {...register('house',{required:'House number is required'})} />
                                {errors.house && <span className="bb-msg">{errors.house.message}</span>}
                            </div>
                            <div className="d-flex flex-wrap gap-2">
                                <div className={`bb-field flex-grow-1 ${errors.area? 'error':''}`} style={{minWidth:'180px'}}>
                                    <FaMapMarkerAlt className="bb-ico" />
                                    <input type="text" placeholder="Area / Locality *" {...register('area',{required:'Area is required'})} />
                                    {errors.area && <span className="bb-msg">{errors.area.message}</span>}
                                </div>
                                <div className="bb-field" style={{minWidth:'140px'}}>
                                    <FaCity className="bb-ico" />
                                    <input type="text" value="Bareilly" disabled />
                                </div>
                            </div>
                            <div className="bb-field">
                                <FaLandmark className="bb-ico" />
                                <input type="text" placeholder="Landmark (optional)" {...register('landmark')} />
                            </div>
                            <div className={`bb-field ${errors.mobile? 'error':''}`}>
                                <FaPhone className="bb-ico" />
                                <input type="text" placeholder="Alt Mobile (optional)" {...register('mobile',{ pattern:{ value:/^[0-9]{10}$/, message:'Invalid mobile number'} })} />
                                {errors.mobile && <span className="bb-msg">{errors.mobile.message}</span>}
                            </div>

                            <button type="submit" className="w-100 p-2 mb-3 btn btn-danger" disabled={loading || mapLoading}>
                                {loading ? (
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                ) : (
                                    'CONTINUE TO PLACE ORDER'
                                )}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
            <div className='mt-5 mb-5 pb-5'></div>
            <ToastContainer/>
        </Fragment>
    );
}

export default NewAddressPage;
